import { gsap } from 'gsap'
import { getSceneImagePath } from '../../../utils/pathResolver'
import { SaveStateManager } from '../../../features/save-state'
import { getAudioManager } from '../../../features/audio'
import {
  SCENE_ID,
  BUBBLE_DISPLAY_MS,
  WRONG_BUBBLE_COUNT,
  FLOWERS,
  TARGETS,
  CORRECT_KINDS,
  type FlowerItem,
  type FlowerKind,
} from './constants'
import { showGlobalScrollIndicator, hideGlobalScrollIndicator } from '../../../utils/scroll-indicator'
import type { SceneData } from '../../scenes/types'
import './HledaniKvetin.css'

interface HledaniKvetinState {
  found: string[]
  completed: boolean
}

function loadState(): HledaniKvetinState {
  const saved = SaveStateManager.load()
  const mg = saved?.minigames?.[SCENE_ID] as Partial<HledaniKvetinState> | undefined
  if (mg) {
    return {
      found: mg.found ?? [],
      completed: mg.completed ?? false,
    }
  }
  return { found: [], completed: false }
}

function saveState(state: HledaniKvetinState): void {
  const saved = SaveStateManager.load() ?? SaveStateManager.createInitialState()
  if (!saved.minigames) saved.minigames = {}
  saved.minigames[SCENE_ID] = {
    found: [...state.found],
    completed: state.completed,
  }
  SaveStateManager.saveImmediate(saved)
}

function isElementInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect()
  return rect.top < window.innerHeight && rect.bottom > 0
}

function findNextSceneWrapper(root: HTMLElement, onFound: (el: HTMLElement) => void): void {
  const MAX_ATTEMPTS = 20
  const INTERVAL_MS = 100
  let attempts = 0

  function check() {
    attempts++
    const sceneWrapper = root.closest('.scene-wrapper')
    if (sceneWrapper) {
      let next = sceneWrapper.nextElementSibling as HTMLElement | null
      while (next && !next.classList.contains('scene-wrapper')) {
        next = next.nextElementSibling as HTMLElement | null
      }
      if (next) {
        onFound(next)
        return
      }
    }
    if (attempts < MAX_ATTEMPTS) {
      setTimeout(check, INTERVAL_MS)
    }
  }

  check()
}

export function createMinigame(container: HTMLElement, onComplete?: () => void, _sceneData?: SceneData): HTMLElement {
  const root = document.createElement('div')
  root.className = 'minigame-hledani-kvetin'
  root.setAttribute('data-minigame', SCENE_ID)

  const state = loadState()
  const foundSet = new Set(state.found)

  // Per-kind wrong-bubble cycling: first shows unique bubble, then cycles general wrong, then unique again...
  const wrongClickCounts: Record<string, number> = {}

  let scrollTarget: HTMLElement | null = null

  // ── Counter panel (sticky at top) ──
  const panel = document.createElement('div')
  panel.className = 'minigame-hledani-kvetin__panel'
  const counterEls: Record<FlowerKind, HTMLSpanElement> = {} as Record<FlowerKind, HTMLSpanElement>

  TARGETS.forEach((target) => {
    const item = document.createElement('div')
    item.className = 'minigame-hledani-kvetin__panel-item'

    const icon = document.createElement('img')
    icon.className = 'minigame-hledani-kvetin__panel-icon'
    icon.src = getSceneImagePath(SCENE_ID, `flowers/${target.kind}.png`)
    icon.alt = target.kind
    item.appendChild(icon)

    const counter = document.createElement('span')
    counter.className = 'minigame-hledani-kvetin__panel-counter'
    counterEls[target.kind] = counter
    item.appendChild(counter)

    panel.appendChild(item)
  })
  root.appendChild(panel)

  // ── Scene area (bg + flowers share the same coordinate space) ──
  const sceneArea = document.createElement('div')
  sceneArea.className = 'minigame-hledani-kvetin__scene-area'

  const bgImg = document.createElement('img')
  bgImg.className = 'minigame-hledani-kvetin__bg'
  bgImg.src = getSceneImagePath(SCENE_ID, 'bg.jpg')
  bgImg.alt = ''
  sceneArea.appendChild(bgImg)

  const itemsContainer = document.createElement('div')
  itemsContainer.className = 'minigame-hledani-kvetin__items'
  sceneArea.appendChild(itemsContainer)

  root.appendChild(sceneArea)

  updateCounters()

  // ── Active bubble tracking ──
  let activeBubbleTimeout: ReturnType<typeof setTimeout> | null = null
  let activeBubbleEl: HTMLElement | null = null

  // ── Create flower buttons (sorted top→bottom, left→right for tab order) ──
  const sortedFlowers = [...FLOWERS].sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x)
  sortedFlowers.forEach((flower) => {
    const btn = document.createElement('button')
    btn.className = 'minigame-hledani-kvetin__flower-btn'
    btn.type = 'button'
    btn.setAttribute('data-flower-id', flower.id)
    btn.setAttribute('aria-label', flower.kind)
    btn.style.left = `${flower.position.x}%`
    btn.style.top = `${flower.position.y}%`
    btn.style.width = `${flower.width}%`

    const img = document.createElement('img')
    img.src = getSceneImagePath(SCENE_ID, `flowers/${flower.kind}.png`)
    img.alt = flower.kind
    img.style.transform = `rotate(${flower.rotationDeg.toFixed(2)}deg)`
    btn.appendChild(img)

    if (foundSet.has(flower.id)) {
      btn.classList.add('minigame-hledani-kvetin__flower-btn--found')
      btn.disabled = true
    }

    if (state.completed) {
      btn.disabled = true
    }

    btn.addEventListener('click', () => onFlowerClick(btn, flower))

    itemsContainer.appendChild(btn)
  })

  // ── Handle already-completed state ──
  if (state.completed) {
    if (onComplete) onComplete()
  }

  // ── Scroll indicator logic ──
  function onScroll() {
    if (!scrollTarget) return
    if (isElementInViewport(scrollTarget)) {
      hideGlobalScrollIndicator()
      scrollTarget = null
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll, { passive: true })
  ;(root as unknown as Record<string, () => void>).__minigameCleanup = () => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onScroll)
    hideGlobalScrollIndicator()
  }

  function updateCounters(): void {
    TARGETS.forEach((target) => {
      const found = state.found.filter((id) => {
        const flower = FLOWERS.find((f) => f.id === id)
        return flower && flower.kind === target.kind
      }).length
      const el = counterEls[target.kind]
      el.textContent = `${found}/${target.total}`
      el.classList.toggle('minigame-hledani-kvetin__panel-counter--complete', found >= target.total)
    })
  }

  function getWrongBubbleSrcAndName(flower: FlowerItem): { src: string; name: string } {
    const key = flower.kind
    const count = wrongClickCounts[key] ?? 0
    wrongClickCounts[key] = count + 1

    const cycleLength = 1 + WRONG_BUBBLE_COUNT
    const pos = count % cycleLength
    const name = pos === 0 ? `bubble-${flower.kind}` : `bubble-wrong-${pos}`
    return { src: getSceneImagePath(SCENE_ID, `${name}.png`), name }
  }

  function showBubbleAtFlower(
    flower: FlowerItem,
    anchorBtn: HTMLButtonElement,
    src: string,
    fallbackMs: number,
    audioFile?: string,
  ): void {
    if (activeBubbleTimeout) {
      clearTimeout(activeBubbleTimeout)
      activeBubbleTimeout = null
    }
    if (activeBubbleEl?.parentNode) {
      activeBubbleEl.parentNode.removeChild(activeBubbleEl)
    }

    const pos: 'top' | 'bottom' = flower.position.y < 50 ? 'bottom' : 'top'
    const bubbleWidth = 50
    const bubble = document.createElement('div')
    bubble.className = 'minigame-hledani-kvetin__bubble'
    bubble.style.width = `${bubbleWidth}%`

    const itemCenterX = flower.position.x + flower.width / 2
    const clampedLeft = Math.max(0, Math.min(itemCenterX - bubbleWidth / 2, 100 - bubbleWidth))
    bubble.style.left = `${clampedLeft}%`

    const containerRect = itemsContainer.getBoundingClientRect()
    const anchorRect = anchorBtn.getBoundingClientRect()
    const anchorTopPercent = ((anchorRect.top - containerRect.top) / containerRect.height) * 100
    const anchorBottomPercent = ((anchorRect.bottom - containerRect.top) / containerRect.height) * 100
    bubble.style.top = `${pos === 'bottom' ? anchorBottomPercent : anchorTopPercent}%`

    const img = document.createElement('img')
    img.src = src
    img.alt = ''
    bubble.appendChild(img)

    if (pos === 'bottom') {
      gsap.set(bubble, { yPercent: 0, scale: 0, transformOrigin: 'center top' })
    } else {
      gsap.set(bubble, { yPercent: -100, scale: 0, transformOrigin: 'center bottom' })
    }

    itemsContainer.appendChild(bubble)
    gsap.to(bubble, { scale: 1, duration: 0.35, ease: 'back.out(1.7)' })

    activeBubbleEl = bubble

    const scheduleDismiss = (ms: number) => {
      activeBubbleTimeout = setTimeout(() => {
        gsap.to(bubble, {
          scale: 0,
          duration: 0.3,
          ease: 'back.in(1.7)',
          onComplete: () => {
            if (bubble.parentNode) bubble.parentNode.removeChild(bubble)
            if (activeBubbleEl === bubble) activeBubbleEl = null
          },
        })
        activeBubbleTimeout = null
      }, ms)
    }

    if (audioFile) {
      getAudioManager()
        .tryPlayCustomSound(audioFile, SCENE_ID, 'speech')
        .then((durationMs) => {
          if (activeBubbleEl !== bubble) return
          scheduleDismiss(durationMs > 0 ? durationMs + 500 : fallbackMs)
        })
    } else {
      scheduleDismiss(fallbackMs)
    }
  }

  function onFlowerClick(btn: HTMLButtonElement, flower: FlowerItem): void {
    if (btn.disabled) return

    const isCorrect = CORRECT_KINDS.has(flower.kind)

    if (isCorrect) {
      btn.classList.add('minigame-hledani-kvetin__flower-btn--found')
      btn.disabled = true
      foundSet.add(flower.id)

      if (!state.found.includes(flower.id)) {
        state.found.push(flower.id)
      }
      saveState(state)
      updateCounters()

      const foundOfKind = state.found.filter((id) => {
        const f = FLOWERS.find((fl) => fl.id === id)
        return f && f.kind === flower.kind
      }).length
      const bubbleName = `bubble-${flower.kind}-${foundOfKind}`
      const bubbleSrc = getSceneImagePath(SCENE_ID, `${bubbleName}.png`)
      showBubbleAtFlower(flower, btn, bubbleSrc, BUBBLE_DISPLAY_MS, `${bubbleName}.mp3`)

      checkCompletion()
    } else {
      const { src: bubbleSrc, name: bubbleName } = getWrongBubbleSrcAndName(flower)
      showBubbleAtFlower(flower, btn, bubbleSrc, BUBBLE_DISPLAY_MS, `${bubbleName}.mp3`)
    }
  }

  function checkCompletion(): void {
    const allFound = TARGETS.every((target) => {
      const count = state.found.filter((id) => {
        const f = FLOWERS.find((fl) => fl.id === id)
        return f && f.kind === target.kind
      }).length
      return count >= target.total
    })

    if (!allFound) return

    state.completed = true
    saveState(state)

    itemsContainer.querySelectorAll<HTMLButtonElement>('.minigame-hledani-kvetin__flower-btn').forEach((b) => {
      b.disabled = true
    })

    setTimeout(() => {
      if (onComplete) onComplete()
      showGlobalScrollIndicator()
      findNextSceneWrapper(root, (el) => {
        scrollTarget = el
      })
    }, 800)
  }

  return root
}
